import { formatDate } from "@repo/shared/i18n";
import DOMPurify from "dompurify";
import { marked } from "marked";

// We can put this somewhere safer
export const STRAPI_URL = import.meta.env.VITE_APP_STRAPI_URL;

export type Media = {
  url?: string;
  alternativeText?: string | null;
  caption?: string | null;
  formats?: {
    small?: { url?: string };
    medium?: { url?: string };
    large?: { url?: string };
  };
};

export type Block =
  | { id: number; __component: "shared.rich-text"; body: string }
  | {
      id: number;
      __component: "shared.quote";
      body: string;
      title?: string | null;
    }
  | { id: number; __component: "shared.media"; file?: Media; [k: string]: any };

export type Article = {
  id: number;
  title: string;
  description?: string | null;
  slug?: string | null;
  cover?: Media | null;
  author?: { name?: string | null } | null;
  category?: { name?: string | null } | null;
  publishedAt: string | null;
  blocks?: Block[] | null;
  visibleOnBeta?: boolean | null;
};

const normalizeSrc = (u?: string | null): string | undefined => {
  if (!u) return undefined;
  if (u.startsWith("http://") || u.startsWith("https://")) return u;
  if (u.startsWith("/")) return `${STRAPI_URL}${u}`;
  return u;
};

/** "19 Oct 2026": one byline date for every article card and page. */
export const formatArticleDate = (publishedAt: string): string => {
  const date = new Date(publishedAt);
  const month = formatDate(date, { month: "short" });
  return `${date.getDate()} ${month} ${date.getFullYear()}`;
};

export const imgUrl = (m?: Media | null): string | undefined =>
  normalizeSrc(m?.formats?.small?.url || m?.url);

export const imageSrcFromBlock = (block: any): string | null => {
  if (!block || block.__component !== "shared.media" || !block.file)
    return null;
  return (
    normalizeSrc(block.file.formats?.medium?.url) ||
    normalizeSrc(block.file.formats?.large?.url) ||
    normalizeSrc(block.file.formats?.small?.url) ||
    normalizeSrc(block.file.url) ||
    null
  );
};

marked.use({ async: false });
marked.setOptions({ gfm: true, breaks: false });

const renderer: any = {
  link({ href, title, text }: any) {
    const t = title ? ` title="${title}"` : "";
    const safeHref = href ?? "#";
    const inner = text ?? href ?? "";
    return `<a href="${safeHref}"${t} target="_blank" rel="noopener noreferrer">${inner}</a>`;
  },
};

marked.use({ renderer });

export const prepNewlines = (md: string): string =>
  md.replace(/\r\n?/g, "\n").replace(/([^\n])\n(?!\n)/g, "$1  \n");

export function styleHeadings(html: string): string {
  const add = (lvl: 1 | 2 | 3 | 4, cls: string) => {
    html = html.replace(
      new RegExp(`<h${lvl}([^>]*?)class="([^"]*)"`, "g"),
      `<h${lvl}$1 class="$2 ${cls}"`,
    );
    html = html.replace(
      new RegExp(`<h${lvl}([^>]*)>`, "g"),
      `<h${lvl}$1 class="${cls}">`,
    );
    return html;
  };
  html = add(1, "mt-6 mb-2 text-2xl md:text-3xl font-bold");
  html = add(2, "mt-5 mb-2 text-xl md:text-2xl font-semibold");
  html = add(3, "mt-4 mb-1 text-lg md:text-xl font-semibold");
  html = add(4, "mt-4 mb-1 text-base md:text-lg font-semibold");
  return html;
}

export function mdToHtml(md: string): string {
  const normalized = md.replace(/\r\n?/g, "\n");
  let html = marked.parse(normalized, { async: false }) as string;
  html = styleHeadings(html);
  return DOMPurify.sanitize(html, {
    ADD_ATTR: ["target", "rel"],
  });
}

const YT_RE =
  /https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtube\.com\/shorts\/|youtu\.be\/)([A-Za-z0-9_-]{6,})/i;

const toEmbed = (url: string): string | null => {
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, "");
    if (host === "youtu.be")
      return `https://www.youtube.com/embed/${u.pathname.slice(1)}`;
    if (host === "youtube.com" || host === "m.youtube.com") {
      const v = u.searchParams.get("v");
      if (v) return `https://www.youtube.com/embed/${v}`;
      const p = u.pathname.split("/").filter(Boolean);
      if (p[0] === "embed" && p[1])
        return `https://www.youtube.com/embed/${p[1]}`;
      if (p[0] === "shorts" && p[1])
        return `https://www.youtube.com/embed/${p[1]}`;
    }
  } catch {}
  return null;
};

export const extractYouTube = (md: string): { embed?: string; md: string } => {
  const m = md.match(YT_RE);
  if (!m) return { md };
  const url = m[0];
  const embed = toEmbed(url) || undefined;
  return { embed, md: md.replace(url, "").trim() };
};

export const visibilityFilter = (hostname: string): string => {
  const isLocal = hostname === "localhost";
  const isBeta = hostname.startsWith("beta.");
  // beta: show all; local: show all
  if (isBeta || isLocal) return "";
  // staging + prod: only where testArticle is false (or unset)
  return (
    `&filters[$or][0][testArticle][$eq]=false` +
    `&filters[$or][1][testArticle][$null]=true`
  );
};
